import type {
  CollectDataTemplateActionDto,
  CollectDataTemplateCreateConfigPayloadDto,
  CollectDataTemplateCreateStepActionDto,
  CollectDataTemplateCreateStepPayloadDto,
  CollectDataTemplateStepActionDto,
  CollectDataTemplateStepCreateMetadataDto,
  CollectDataTemplateStepUpdateMetadataDto,
  CollectDataTemplateUpdateConfigPayloadDto,
  CollectDataTemplateUpdateStepPayloadDto,
} from 'src/interfaces/collectDataTemplateUpdate'
import {
  CollectDataTemplateStepEvents,
  CollectDataTemplateStepsConfigEvents,
} from 'src/interfaces/collectDataTemplateUpdate'
import type {
  CollectDataTemplateStepFormMetadata,
  CollectDataTemplateStepMetadataFields,
  CollectDataTemplateStepRow,
  CollectDataTemplateStepsConfig,
  CollectDataFormTemplateItem,
  OnboardingTemplate,
  TemplateFormItem,
} from 'src/interfaces/onboardingTemplate'
import { CollectDataFormItemType, CollectDataStepType } from 'src/constants/collect-data/enums'
import { isFlowEditorNewlyAddedStepId } from 'src/constants/flowEditorFeatureFlags'

function hasMeaningfulMetadata(
  metadata: CollectDataTemplateStepMetadataFields | null | undefined,
): boolean {
  if (metadata === null || metadata === undefined) {
    return false
  }

  return Object.keys(metadata).length > 0
}

/**
 * Collect-data template API expects text blocks as `{ type, id, text }`, while the
 * flow editor uses `content`. Map on the way to PUT payloads only.
 */
function mapFormItemsToCollectDataTemplateApi(formItems: TemplateFormItem[]): TemplateFormItem[] {
  return formItems.map((item) => {
    if (item.type !== CollectDataFormItemType.TextBlock) {
      return item
    }

    const raw = item as TemplateFormItem & { text?: string; content?: string }
    let text = ''

    if (typeof raw.text === 'string' && raw.text.trim() !== '') {
      text = raw.text.trim()
    } else if (typeof raw.content === 'string') {
      text = raw.content.trim()
    }

    return { id: raw.id, type: raw.type, text } as unknown as TemplateFormItem
  })
}

/**
 * Nest rejects `metadata: null` and requires a non-empty object for form steps.
 * Omit `metadata` entirely when there is nothing to send.
 */
function ensureFormMetadataForApi(
  row: CollectDataTemplateStepRow,
): CollectDataTemplateStepFormMetadata {
  const metadata = row.metadata ?? {}
  const formItemsRaw = Array.isArray(metadata.formItems) ? metadata.formItems : []
  const formItems = mapFormItemsToCollectDataTemplateApi(
    formItemsRaw as TemplateFormItem[],
  ) as CollectDataFormTemplateItem[]
  const title = typeof metadata.title === 'string' ? metadata.title.trim() : ''
  const description = typeof metadata.description === 'string' ? metadata.description.trim() : ''

  return { title, description, formItems }
}

function ensureInsuranceMetadataForApi(
  row: CollectDataTemplateStepRow,
): CollectDataTemplateStepMetadataFields {
  const metadata = row.metadata ?? {}

  if (
    typeof metadata.insurancePolicyProductId === 'string' &&
    metadata.insurancePolicyProductId.trim() !== ''
  ) {
    return { insurancePolicyProductId: metadata.insurancePolicyProductId.trim() }
  }

  return { insurancePolicyProductId: '' }
}

function ensureYardstikMetadataForApi(
  row: CollectDataTemplateStepRow,
): CollectDataTemplateStepMetadataFields {
  const metadata = row.metadata ?? {}
  const normalized: CollectDataTemplateStepMetadataFields = {
    enableProvider: Boolean(metadata.enableProvider),
    enableManualUpload: Boolean(metadata.enableManualUpload),
  }

  if (metadata.provider) {
    normalized.provider = metadata.provider
  }

  if (typeof metadata.packageId === 'string' && metadata.packageId.trim() !== '') {
    normalized.packageId = metadata.packageId.trim()
  }

  return normalized
}

export function metadataForApiStepPayload(
  row: CollectDataTemplateStepRow,
): CollectDataTemplateStepCreateMetadataDto | CollectDataTemplateStepUpdateMetadataDto | undefined {
  if (row.type === CollectDataStepType.Form) {
    return ensureFormMetadataForApi(row)
  }

  if (row.type === CollectDataStepType.InsuranceCoverage) {
    return ensureInsuranceMetadataForApi(row)
  }

  if (row.type === CollectDataStepType.BGC || row.type === CollectDataStepType.MVR) {
    return ensureYardstikMetadataForApi(row)
  }

  if (row.type === CollectDataStepType.VehicleInsurance) {
    const metadata = row.metadata ?? {}

    return {
      disableInsuranceUpload: Boolean(metadata.disableInsuranceUpload),
    }
  }

  if (hasMeaningfulMetadata(row.metadata)) {
    return row.metadata as CollectDataTemplateStepCreateMetadataDto
  }

  return undefined
}

function resolvedStepRowLabel(row: CollectDataTemplateStepRow): string {
  if (typeof row.label === 'string' && row.label.trim() !== '') {
    return row.label
  }

  return String(row.type)
}

export function rowToUpdatePayload(
  row: CollectDataTemplateStepRow,
): CollectDataTemplateUpdateStepPayloadDto {
  const metadata = metadataForApiStepPayload(row) as
    | CollectDataTemplateStepUpdateMetadataDto
    | undefined
  const base: CollectDataTemplateUpdateStepPayloadDto = {
    id: row.id,
    type: row.type,
    label: resolvedStepRowLabel(row),
    order: row.order,
    optional: row.optional,
    retroactiveCollectRequired: row.retroactiveCollectRequired,
    expiresInDays: row.expiresInDays ?? undefined,
    requireExpirationDate: row.requireExpirationDate,
    maxAttempts: row.maxAttempts,
    approvalType: row.approvalType,
    ignoreMonitoring: row.ignoreMonitoring,
    continueOnIncomplete: row.continueOnIncomplete,
    recollectSameTypeSteps: row.recollectSameTypeSteps,
    skipRetroactiveCollectIfAnySameTypeCompleted: row.skipRetroactiveCollectIfAnySameTypeCompleted,
  }

  if (metadata !== undefined) {
    return { ...base, metadata }
  }

  return base
}

export function rowToCreatePayload(
  row: CollectDataTemplateStepRow,
): CollectDataTemplateCreateStepPayloadDto {
  const metadata = metadataForApiStepPayload(row) as
    | CollectDataTemplateStepCreateMetadataDto
    | undefined
  const base: CollectDataTemplateCreateStepPayloadDto = {
    type: row.type,
    label: resolvedStepRowLabel(row),
    order: row.order,
    optional: row.optional,
    retroactiveCollectRequired: row.retroactiveCollectRequired,
    expiresInDays: row.expiresInDays ?? undefined,
    requireExpirationDate: row.requireExpirationDate,
    maxAttempts: row.maxAttempts,
    approvalType: row.approvalType,
    ignoreMonitoring: row.ignoreMonitoring,
    continueOnIncomplete: row.continueOnIncomplete,
    recollectSameTypeSteps: row.recollectSameTypeSteps,
    skipRetroactiveCollectIfAnySameTypeCompleted: row.skipRetroactiveCollectIfAnySameTypeCompleted,
  }

  if (metadata !== undefined) {
    return { ...base, metadata }
  }

  return base
}

function rowsEqual(a: CollectDataTemplateStepRow, b: CollectDataTemplateStepRow): boolean {
  return JSON.stringify(rowToUpdatePayload(a)) === JSON.stringify(rowToUpdatePayload(b))
}

export type BuildCollectDataTemplateUpdateActionsOptions = {
  /** When set, only existing steps in this set can emit `step_updated`. */
  unsavedStepIds?: Set<string>
  /** When set, only configs in this set can emit `config_updated` without step actions. */
  unsavedConfigIds?: Set<string>
}

function buildStepActionsForConfig(
  prevConfig: CollectDataTemplateStepsConfig | undefined,
  nextConfig: CollectDataTemplateStepsConfig,
  options?: BuildCollectDataTemplateUpdateActionsOptions,
): CollectDataTemplateStepActionDto[] {
  const prevRows = prevConfig?.collectDataTemplateSteps ?? []
  const nextRows = nextConfig.collectDataTemplateSteps ?? []
  const prevById = new Map(prevRows.map((r) => [r.id, r]))
  const nextById = new Map(nextRows.map((r) => [r.id, r]))
  const stepActions: CollectDataTemplateStepActionDto[] = []

  for (const row of nextRows) {
    if (isFlowEditorNewlyAddedStepId(row.id)) {
      stepActions.push({
        type: CollectDataTemplateStepEvents.Created,
        payload: rowToCreatePayload(row),
      })

      continue
    }

    const prevRow = prevById.get(row.id)

    if (prevRow && !rowsEqual(prevRow, row)) {
      if (options?.unsavedStepIds && !options.unsavedStepIds.has(row.id)) {
        continue
      }

      stepActions.push({
        type: CollectDataTemplateStepEvents.Updated,
        payload: rowToUpdatePayload(row),
      })
    }
  }

  for (const prevRow of prevRows) {
    if (nextById.has(prevRow.id)) {
      continue
    }

    if (isFlowEditorNewlyAddedStepId(prevRow.id)) {
      continue
    }

    stepActions.push({
      type: CollectDataTemplateStepEvents.Deleted,
      payload: { id: prevRow.id },
    })
  }

  return stepActions
}

/** Non-empty trimmed id, or `undefined` to omit the key (Nest rejects `insurancePolicyId: null`). */
export function insurancePolicyIdForApiIfPresent(
  value: string | null | undefined,
): string | undefined {
  if (value === null || value === undefined) {
    return undefined
  }

  const trimmed = String(value).trim()

  if (trimmed === '') {
    return undefined
  }

  return trimmed
}

/** `config_updated` payload — Nest `CollectDataTemplateUpdateConfigPayloadDto` has no `insurancePolicyId`. */
function configUpdatePayload(
  nextConfig: CollectDataTemplateStepsConfig,
  stepActions: CollectDataTemplateStepActionDto[],
): CollectDataTemplateUpdateConfigPayloadDto {
  const title =
    typeof nextConfig.title === 'string' && nextConfig.title.trim() !== ''
      ? nextConfig.title.trim()
      : undefined
  const description =
    typeof nextConfig.description === 'string' && nextConfig.description.trim() !== ''
      ? nextConfig.description.trim()
      : undefined

  return {
    id: nextConfig.id,
    label: nextConfig.label,
    delayed: nextConfig.delayed,
    order: nextConfig.order,
    title,
    description,
    stepActions: stepActions.length > 0 ? stepActions : undefined,
  }
}

/** `config_created` — no config `id` (Nest assigns). `stepActions` must be non-empty `step_created` only. */
function configCreatePayload(
  nextConfig: CollectDataTemplateStepsConfig,
  stepActions: CollectDataTemplateCreateStepActionDto[],
): CollectDataTemplateCreateConfigPayloadDto {
  const insurancePolicyId = insurancePolicyIdForApiIfPresent(nextConfig.insurancePolicyId)
  const payload: CollectDataTemplateCreateConfigPayloadDto = {
    label: nextConfig.label,
    delayed: nextConfig.delayed,
    order: nextConfig.order,
    stepActions,
  }

  const withPolicy = insurancePolicyId !== undefined ? { ...payload, insurancePolicyId } : payload

  if (typeof nextConfig.title === 'string' && nextConfig.title.trim() !== '') {
    return { ...withPolicy, title: nextConfig.title.trim() }
  }

  return withPolicy
}

/**
 * Builds the PUT body for `CollectDataTemplateUpdateDto` by diffing the last-loaded template
 * against the desired template (including local step / config edits).
 */
export function buildCollectDataTemplateUpdateActions(
  prev: OnboardingTemplate,
  next: OnboardingTemplate,
  options?: BuildCollectDataTemplateUpdateActionsOptions,
): CollectDataTemplateActionDto[] {
  const actions: CollectDataTemplateActionDto[] = []
  const prevConfigs = prev.collectDataTemplateStepsConfigs ?? []
  const nextConfigs = next.collectDataTemplateStepsConfigs ?? []
  const prevById = new Map(prevConfigs.map((c) => [c.id, c]))
  const nextById = new Map(nextConfigs.map((c) => [c.id, c]))

  let anyMutation = false

  for (const prevConfig of prevConfigs) {
    if (!nextById.has(prevConfig.id)) {
      anyMutation = true

      break
    }
  }

  const diffByConfigId = new Map<string, CollectDataTemplateStepActionDto[]>()

  for (const nextConfig of nextConfigs) {
    const prevConfig = prevById.get(nextConfig.id)

    if (!prevConfig) {
      const diff = buildStepActionsForConfig(undefined, nextConfig, options)

      diffByConfigId.set(nextConfig.id, diff)

      if (diff.length > 0) {
        anyMutation = true
      }

      continue
    }

    const diff = buildStepActionsForConfig(prevConfig, nextConfig, options)

    diffByConfigId.set(nextConfig.id, diff)

    const configFieldsChanged =
      prevConfig.label !== nextConfig.label ||
      prevConfig.delayed !== nextConfig.delayed ||
      prevConfig.order !== nextConfig.order ||
      prevConfig.title !== nextConfig.title ||
      prevConfig.insurancePolicyId !== nextConfig.insurancePolicyId

    if (diff.length > 0 || configFieldsChanged) {
      anyMutation = true
    }
  }

  if (!anyMutation) {
    return actions
  }

  for (const nextConfig of nextConfigs) {
    const prevConfig = prevById.get(nextConfig.id)

    if (!prevConfig) {
      const diff = diffByConfigId.get(nextConfig.id) ?? []
      const createStepActions: CollectDataTemplateCreateStepActionDto[] = []

      for (const a of diff) {
        if (a.type === CollectDataTemplateStepEvents.Created) {
          createStepActions.push(a)
        }
      }

      if (createStepActions.length === 0) {
        continue
      }

      actions.push({
        type: CollectDataTemplateStepsConfigEvents.Created,
        payload: configCreatePayload(nextConfig, createStepActions),
      })

      continue
    }

    const diff = diffByConfigId.get(nextConfig.id) ?? []
    const configFieldsChanged =
      prevConfig.label !== nextConfig.label ||
      prevConfig.delayed !== nextConfig.delayed ||
      prevConfig.order !== nextConfig.order ||
      prevConfig.title !== nextConfig.title ||
      prevConfig.insurancePolicyId !== nextConfig.insurancePolicyId

    if (diff.length === 0 && !configFieldsChanged) {
      continue
    }

    if (
      diff.length === 0 &&
      configFieldsChanged &&
      options?.unsavedConfigIds &&
      !options.unsavedConfigIds.has(nextConfig.id)
    ) {
      continue
    }

    actions.push({
      type: CollectDataTemplateStepsConfigEvents.Updated,
      payload: configUpdatePayload(nextConfig, diff),
    })
  }

  for (const prevConfig of prevConfigs) {
    if (nextById.has(prevConfig.id)) {
      continue
    }

    actions.push({
      type: CollectDataTemplateStepsConfigEvents.Deleted,
      payload: { id: prevConfig.id },
    })
  }

  return actions
}
